const SESSION_KEY = "sg_landing_url";

const DOMAIN_RE =
  /^(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/i;

export type NormalizedWebsite =
  | { ok: true; href: string; domain: string }
  | { ok: false; reason: "empty" | "malformed" };

/** Accept bare domains (acmept.com) and full URLs. Normalize to https on success. */
export function normalizeBusinessWebsite(raw: string): NormalizedWebsite {
  const trimmed = raw.trim();
  if (!trimmed) return { ok: false, reason: "empty" };

  const withProtocol = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
  let url: URL;
  try {
    url = new URL(withProtocol);
  } catch {
    return { ok: false, reason: "malformed" };
  }

  if (url.protocol !== "http:" && url.protocol !== "https:") {
    return { ok: false, reason: "malformed" };
  }

  const domain = url.hostname.replace(/^www\./i, "").toLowerCase();
  if (
    !domain ||
    domain === "localhost" ||
    domain.endsWith(".local") ||
    !DOMAIN_RE.test(domain) ||
    !domain.includes(".")
  ) {
    return { ok: false, reason: "malformed" };
  }

  const path =
    url.pathname === "/" ? "" : url.pathname.replace(/\/$/, "");
  const href = `https://${domain}${path}${url.search}`;
  return { ok: true, href, domain };
}

/** Normalize a query/session URL for the public scanner, or return the trimmed raw value. */
export function resolveScannerPrefillUrl(raw: string | null | undefined): string {
  const trimmed = raw?.trim() ?? "";
  if (!trimmed) return "";
  const normalized = normalizeBusinessWebsite(trimmed);
  return normalized.ok ? normalized.href : trimmed;
}

export function persistLandingUrl(href: string): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(SESSION_KEY, href);
  } catch {
    /* private mode */
  }
}

export function readLandingUrl(): string | null {
  if (typeof window === "undefined") return null;
  try {
    const value = window.sessionStorage.getItem(SESSION_KEY)?.trim();
    return value || null;
  } catch {
    return null;
  }
}

export const URL_HELPER_INVALID =
  "That doesn't look like a website - try acmept.com.";
